import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Switch,
  TextField,
  Typography
} from '@mui/material';
import {
  CheckCircle as OkIcon,
  Description as DocumentIcon,
  ErrorOutline as ProblemIcon,
  RemoveCircleOutline as PendingIcon,
  WarningAmber as WarnIcon
} from '@mui/icons-material';
import { doc, onSnapshot, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { fetchVehicles } from '../../redux/slices/vehiclesSlice';
import { SPECIAL_CAPABILITIES } from '../../config/vehicleTypes';
import { getBillingFacts, getTrackVerdict } from './billingFacts';
import { DMRZ_FORMAT_OPTIONS, DEFAULT_DMRZ_FORMAT } from './dmrzFahrtenimport';

/**
 * Einrichtungs-Assistent für die Kassenabrechnung
 * (nextcab_mobile/docs/general/krankenfahrten_tarife_konzept.md §8.3, DMRZ-
 * Onboarding in krankenfahrten_dmrz_abrechnung_konzept.md §4.3.4).
 *
 * Führt den Unternehmer von „ich rechne heute manuell ab" zu „mein Export
 * passt". Die Angaben landen in `appSettings/medicalBilling`.
 *
 * WICHTIG: In dieses Dokument gehören ausschließlich Betriebsstammdaten
 * (Institutionskennzeichen, Abrechnungs-/Tarifkennzeichen, Verkehrsform,
 * Bezeichnungen für den Stammdaten-Abgleich) — das sind Geschäftsidentifikatoren,
 * keine Geheimnisse. Zugangsdaten für eine spätere API-Anbindung dürfen hier
 * NICHT hinein: `appSettings` ist per Firestore-Rules öffentlich lesbar
 * (`allow read: if true`, damit die App Store-URLs und Programmstatus ohne
 * Login lesen kann).
 */

const PROVIDER_OPTIONS = [
  {
    value: 'dmrz',
    label: 'DMRZ (Deutsches Medizinrechenzentrum)',
    hint: 'Abrechnung nach § 302 SGB V über die DMRZ-Plattform. Benötigt Abrechnungscode und Tarifkennzeichen aus Ihrem DMRZ-Konto.'
  },
  {
    value: 'optadata',
    label: 'opta data / CareMan',
    hint: 'Abrechnung über opta data, Erfassung in CareMan Office. Der Export wird dort importiert.'
  },
  {
    value: 'none',
    label: 'Kein Abrechnungszentrum',
    hint: 'Sie rechnen selbst oder auf Papier ab. Der CSV-Export steht trotzdem zur Verfügung.'
  }
];

const TRANSPORT_MODE_OPTIONS = [
  { value: 'taxi', label: 'Taxi' },
  { value: 'mietwagen', label: 'Mietwagen' }
];

const EMPTY_CONFIG = {
  provider: '',
  ownIk: '',
  operatorName: '',
  billingCode: '',
  tariffCode: '',
  transportMode: 'taxi',
  exportMethod: 'csv',
  // DMRZ-Beauftragung: Kundennummer und Versanddatum der Unterlagen. Beides
  // sind Vorgangsdaten, keine Zugangsdaten — sie dürfen hier stehen.
  dmrzCustomerNumber: '',
  dmrzDocumentsSentAt: '',
  // Stammdaten-Abgleich (Konzept §4.3.3): Diese Bezeichnungen müssen im
  // DMRZ-Zugang existieren, sonst weist DMRZ die ganze Importdatei zurück.
  dmrzTransportCategory: '',
  dmrzVehicleNames: {},
  dmrzCapabilityMap: {},
  dmrzUseLocationIk: false,
  dmrzFormat: {}
};

/**
 * Die beim Kunden zu unterschreibenden DMRZ-Unterlagen.
 *
 * Bewusst NUR die beiden Vertragsdokumente: Die CSV-Spezifikation der
 * Fahrtenimport-Schnittstelle fällt unter das Weitergabeverbot der API-AGB
 * (Ziff. 5a) und wird deshalb nicht bereitgestellt — sie wird hier ohnehin
 * nicht gebraucht, denn die Importdatei erzeugt NextCab.
 */
const DMRZ_DOCUMENTS = [
  {
    key: 'offer',
    href: '/downloads/dmrz/DMRZ-Angebot-Fahrtenimport.pdf',
    label: 'Angebot „Kundenspezifischer Import"',
    hint: 'Einmalig 350 € zzgl. MwSt. Tragen Sie Ihre DMRZ-Kundennummer und die Firmenanschrift ein.'
  },
  {
    key: 'terms',
    href: '/downloads/dmrz/DMRZ-AGB-OpenApi.pdf',
    label: 'AGB zur Nutzung der OpenApi',
    hint: 'Regelt die Datenübergabe per Schnittstelle. Ohne diese AGB gibt DMRZ die Zugangsdaten nicht heraus.'
  }
];

// Werte kommen aus Firestore und koennen null/Zahl sein — nie direkt .trim().
const asText = (value) => (value === null || value === undefined ? '' : String(value));
const isNineDigits = (value) => /^\d{9}$/.test(asText(value).trim());

/**
 * Vollständigkeitsprüfung für den Prüflauf (letzter Schritt). Bewusst keine
 * Datei, sondern eine Checkliste: Der Unternehmer soll sehen, was seinem Export
 * noch fehlt, bevor er ihn beim Abrechnungszentrum einreicht.
 */
const buildReadinessChecks = (rides, config) => {
  const relevant = rides.filter(ride => (ride.medicalTransportBillingStatus || 'not_ready') !== 'paid');
  const count = (predicate) => relevant.filter(predicate).length;

  const checks = [
    {
      key: 'ownIk',
      label: 'Eigenes Institutionskennzeichen hinterlegt',
      problems: isNineDigits(config.ownIk) ? 0 : 1,
      detail: 'Ohne IK Ihres Unternehmens kann kein Abrechnungsfall zugeordnet werden.',
      global: true
    },
    {
      key: 'payerIk',
      label: 'Kostenträgerkennung (IK) je Fahrt',
      problems: count(ride => !(ride.medicalTransportSnapshot || {}).healthInsuranceIk),
      detail: 'Steht auf Muster 4 in der Kopfzeile. Fehlt sie, tragen Sie sie in der Verordnung nach.'
    },
    {
      key: 'insuranceNumber',
      label: 'Versichertennummer je Fahrt',
      problems: count(ride => !(ride.medicalTransportSnapshot || {}).insuranceNumber),
      detail: 'Pflichtangabe für die Abrechnung nach § 302 SGB V.'
    },
    {
      key: 'occupiedKm',
      label: 'Besetzt-Kilometer ermittelt',
      problems: count(ride => {
        const facts = getBillingFacts(ride);
        return !facts || facts.occupiedKm === null || facts.occupiedKm === undefined;
      }),
      detail: 'Werden automatisch aus dem Fahrertracking ermittelt. Fehlen sie, hilft „Neu berechnen" im Tab „Fahrten".'
    },
    {
      key: 'trackQuality',
      label: 'Streckendaten belastbar',
      problems: count(ride => {
        const verdict = getTrackVerdict(ride);
        return verdict === 'review' || verdict === 'unusable';
      }),
      detail: 'Fahrten mit lückenhafter Aufzeichnung sollten Sie vor dem Export ansehen.'
    },
    {
      key: 'tripDirection',
      label: 'Fahrtrichtung eindeutig',
      problems: count(ride => getBillingFacts(ride)?.tripDirection === 'unknown'),
      detail: 'Hin- und Rückfahrt sind getrennte Positionen im Kassenvertrag.'
    },
    {
      key: 'proof',
      label: 'Beförderungsnachweis vorhanden',
      problems: count(ride => {
        const status = ride.medicalTransportProofStatus || 'missing';
        return status !== 'complete' && status !== 'waived';
      }),
      detail: 'Unterschrift des Patienten bzw. dokumentierter Ersatznachweis.'
    }
  ];

  if (config.provider === 'dmrz') {
    checks.splice(1, 0, {
      key: 'tariffCode',
      label: 'Abrechnungscode und Tarifkennzeichen hinterlegt',
      problems: asText(config.billingCode).trim() && asText(config.tariffCode).trim() ? 0 : 1,
      detail: 'DMRZ zieht darüber automatisch den passenden Kassenvertrag.',
      global: true
    });
    // Patientenanschrift und Verordnungsdatum sind eigene DMRZ-Spalten und
    // werden erst seit der Fahrtenimport-Anbindung erfasst — Bestandsverordnungen
    // haben sie deshalb typischerweise noch nicht.
    checks.push({
      key: 'patientAddress',
      label: 'Anschrift des Versicherten erfasst',
      problems: count(ride => {
        const snapshot = ride.medicalTransportSnapshot || {};
        return !(snapshot.patientStreet && snapshot.patientZip && snapshot.patientCity);
      }),
      detail: 'DMRZ führt Straße, Hausnummer, PLZ und Ort getrennt — nachtragbar in der Verordnung.'
    });
    checks.push({
      key: 'transportCategory',
      label: 'Transportkategorie hinterlegt',
      problems: asText(config.dmrzTransportCategory).trim() ? 0 : 1,
      detail: 'Muss im DMRZ-Zugang existieren, sonst weist DMRZ die Importdatei ab.',
      global: true
    });
  }

  return { checks, relevantCount: relevant.length };
};

const MedicalBillingSetupTab = ({ rides, ridesLoading }) => {
  const dispatch = useDispatch();
  const currentUser = useSelector(state => state.auth.user);
  const vehiclesFromStore = useSelector(state => state.appData.vehicles);
  const vehicles = useMemo(() => vehiclesFromStore || [], [vehiclesFromStore]);
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [activeStep, setActiveStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'appSettings', 'medicalBilling'),
      (snapshot) => {
        const data = snapshot.data() || {};
        setConfig({ ...EMPTY_CONFIG, ...data });
        setSavedAt(data.updatedAt || null);
      },
      (snapshotError) => {
        console.error('Abrechnungs-Einstellungen konnten nicht geladen werden:', snapshotError);
        setError('Einstellungen konnten nicht geladen werden.');
      }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (vehicles.length === 0) dispatch(fetchVehicles());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  const readiness = useMemo(() => buildReadinessChecks(rides || [], config), [rides, config]);

  const isDmrz = config.provider === 'dmrz';
  const format = { ...DEFAULT_DMRZ_FORMAT, ...(config.dmrzFormat || {}) };

  const updateField = (field) => (event) => {
    setConfig(previous => ({ ...previous, [field]: event.target.value }));
  };

  const updateSwitch = (field) => (event) => {
    setConfig(previous => ({ ...previous, [field]: event.target.checked }));
  };

  const updateFormatField = (field) => (event) => {
    const raw = event.target.value;
    // Die Select-Werte sind Strings; `quoteFields` ist im Adapter ein Boolean.
    const value = field === 'quoteFields' ? raw === 'true' : raw;
    setConfig(previous => ({
      ...previous,
      dmrzFormat: { ...(previous.dmrzFormat || {}), [field]: value }
    }));
  };

  const updateMapEntry = (mapField, key) => (event) => {
    const value = event.target.value;
    setConfig(previous => ({
      ...previous,
      [mapField]: { ...(previous[mapField] || {}), [key]: value }
    }));
  };

  const persist = async (patch) => {
    setSaving(true);
    setError('');
    try {
      await setDoc(
        doc(db, 'appSettings', 'medicalBilling'),
        {
          ...config,
          ...patch,
          updatedAt: serverTimestamp(),
          updatedBy: currentUser?.uid || null
        },
        { merge: true }
      );
    } catch (saveError) {
      console.error('Abrechnungs-Einstellungen konnten nicht gespeichert werden:', saveError);
      setError(saveError.message || 'Einstellungen konnten nicht gespeichert werden.');
    } finally {
      setSaving(false);
    }
  };

  const saveAndNext = async (patch = {}) => {
    await persist(patch);
    setActiveStep(step => step + 1);
  };

  const ikError = asText(config.ownIk).trim().length > 0 && !isNineDigits(config.ownIk);

  // Nur Fahrzeuge, die auch abrechnungsrelevant unterwegs waren, brauchen eine
  // DMRZ-Bezeichnung — plus alles, was bereits zugeordnet wurde.
  const vehiclePlates = useMemo(() => {
    const fromFleet = vehicles.map(vehicle => asText(vehicle.licensePlate).trim()).filter(Boolean);
    const fromRides = (rides || []).map(ride => asText(ride.driverLicensePlate).trim()).filter(Boolean);
    const fromConfig = Object.keys(config.dmrzVehicleNames || {});
    return [...new Set([...fromFleet, ...fromRides, ...fromConfig])].sort();
  }, [vehicles, rides, config.dmrzVehicleNames]);

  const steps = [
    {
      key: 'provider',
      label: 'Abrechnungsweg wählen',
      completed: !!config.provider,
      nextDisabled: !config.provider,
      content: (
        <RadioGroup value={asText(config.provider)} onChange={updateField('provider')}>
          {PROVIDER_OPTIONS.map(option => (
            <Box key={option.value} sx={{ mb: 1 }}>
              <FormControlLabel
                value={option.value}
                control={<Radio />}
                label={option.label}
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                {option.hint}
              </Typography>
            </Box>
          ))}
        </RadioGroup>
      )
    },
    {
      key: 'company',
      label: 'Stammdaten Ihres Unternehmens',
      completed: isNineDigits(config.ownIk),
      nextDisabled: ikError,
      content: (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Institutionskennzeichen (IK)"
              value={asText(config.ownIk)}
              onChange={updateField('ownIk')}
              error={ikError}
              helperText={ikError ? 'Ein IK hat genau 9 Ziffern.' : 'Ihr eigenes IK, vergeben von der ARGE·IK'}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextField
              label="Name des Leistungserbringers"
              value={asText(config.operatorName)}
              onChange={updateField('operatorName')}
              helperText="Wie im Vertrag mit den Kassen hinterlegt"
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth>
              <InputLabel id="transport-mode-label">Verkehrsform</InputLabel>
              <Select
                labelId="transport-mode-label"
                label="Verkehrsform"
                value={asText(config.transportMode) || 'taxi'}
                onChange={updateField('transportMode')}
              >
                {TRANSPORT_MODE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {isDmrz && (
            <>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  label="Abrechnungscode"
                  value={asText(config.billingCode)}
                  onChange={updateField('billingCode')}
                  helperText="z. B. 46"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 3 }}>
                <TextField
                  label="Tarifkennzeichen"
                  value={asText(config.tariffCode)}
                  onChange={updateField('tariffCode')}
                  helperText="z. B. 21H65"
                  fullWidth
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  Über Abrechnungscode und Tarifkennzeichen zieht DMRZ
                  automatisch den passenden Kassenvertrag und berechnet
                  Netto, Brutto, Umsatzsteuer und Zuzahlung selbst.
                  <strong> Preise liefern wir nicht</strong> — wir liefern
                  Fahrt-, Patienten- und Verordnungsdaten samt
                  Besetzt-Kilometern.
                </Alert>
              </Grid>
            </>
          )}
        </Grid>
      )
    },
    isDmrz && {
      key: 'order',
      label: 'Fahrtenimport bei DMRZ beauftragen',
      completed: !!asText(config.dmrzDocumentsSentAt).trim(),
      content: (
        <Box>
          {/* Klarstellung von DMRZ vom 30.07.2026: Vertragspartner ist der
              Endkunde. Unsere Entwickler-AGB ersetzt seine Unterschrift nicht —
              das ist der häufigste Stolperstein und steht deshalb ganz oben. */}
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>Sie sind Vertragspartner von DMRZ, nicht wir</AlertTitle>
            Beide Dokumente müssen von <strong>Ihrem Unternehmen</strong>
            {' '}ausgefüllt und unterschrieben werden. NextCab kann das nicht für
            Sie übernehmen — wir liefern nur die Importdatei.
          </Alert>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Laden Sie beide Dokumente herunter, füllen Sie sie aus und senden
            Sie sie unterschrieben an DMRZ zurück. Erst danach richtet DMRZ den
            Import für Ihren Zugang ein und gibt die Schnittstelle frei.
          </Typography>

          <Stack spacing={1.5} sx={{ mb: 2 }}>
            {DMRZ_DOCUMENTS.map(document => (
              <Paper key={document.key} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={1.5} alignItems="flex-start">
                  <DocumentIcon color="action" fontSize="small" sx={{ mt: 0.3 }} />
                  <Box sx={{ flexGrow: 1 }}>
                    <Link href={document.href} target="_blank" rel="noopener" download>
                      {document.label}
                    </Link>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                      {document.hint}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            ))}
          </Stack>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Ihre DMRZ-Kundennummer"
                value={asText(config.dmrzCustomerNumber)}
                onChange={updateField('dmrzCustomerNumber')}
                helperText="Steht in Ihrem DMRZ-Zugang und gehört in beide Formulare."
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Unterlagen an DMRZ gesendet am"
                type="date"
                value={asText(config.dmrzDocumentsSentAt)}
                onChange={updateField('dmrzDocumentsSentAt')}
                slotProps={{ inputLabel: { shrink: true } }}
                helperText="Nur zur eigenen Nachverfolgung — wir übermitteln nichts."
                fullWidth
              />
            </Grid>
          </Grid>

          <Alert severity="info">
            <AlertTitle>Was Sie danach erwartet</AlertTitle>
            DMRZ richtet den kundenspezifischen Import ein (einmalig 350 €
            zzgl. MwSt., direkt an DMRZ zu zahlen) und stellt die Zugangsdaten
            für die Schnittstelle bereit. Bis dahin funktioniert der Export
            trotzdem: Sie laden die Datei im Tab „Fahrten" herunter und in
            Ihrem DMRZ-Zugang hoch.
          </Alert>
        </Box>
      )
    },
    isDmrz && {
      key: 'masterdata',
      label: 'Bezeichnungen mit Ihrem DMRZ-Zugang abgleichen',
      completed: !!asText(config.dmrzTransportCategory).trim(),
      content: (
        <Box>
          <Alert severity="warning" sx={{ mb: 2 }}>
            DMRZ akzeptiert nur Bezeichnungen, die in Ihrem Zugang bereits
            angelegt sind. Weicht eine ab, wird die <strong>gesamte
            Importdatei</strong> abgewiesen — nicht nur die betroffene Zeile.
            Schreiben Sie die Werte deshalb exakt so ab, wie sie im DMRZ-Portal
            stehen.
          </Alert>

          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                label="Transportkategorie"
                value={asText(config.dmrzTransportCategory)}
                onChange={updateField('dmrzTransportCategory')}
                helperText="Gilt für alle Krankenfahrten dieses Betriebs."
                fullWidth
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControlLabel
                control={(
                  <Switch
                    checked={config.dmrzUseLocationIk === true}
                    onChange={updateSwitch('dmrzUseLocationIk')}
                  />
                )}
                label="Standortverwaltung nutzen"
              />
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                Nur einschalten, wenn Ihr DMRZ-Zugang mehrere Leistungserbringer
                führt. Dann wird Ihr IK je Fahrt mitgesendet.
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Fahrzeuge</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Leer gelassen wird das Kennzeichen übertragen — das passt nur, wenn
            das Fahrzeug im DMRZ-Zugang genauso heißt.
          </Typography>
          {vehiclePlates.length === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Noch keine Fahrzeuge bekannt. Sobald Krankenfahrten gefahren
              wurden, erscheinen sie hier.
            </Alert>
          ) : (
            <Grid container spacing={2} sx={{ mb: 2 }}>
              {vehiclePlates.map(plate => (
                <Grid key={plate} size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label={plate}
                    value={asText((config.dmrzVehicleNames || {})[plate])}
                    onChange={updateMapEntry('dmrzVehicleNames', plate)}
                    placeholder={plate}
                    helperText="Bezeichnung im DMRZ-Zugang"
                    fullWidth
                    size="small"
                  />
                </Grid>
              ))}
            </Grid>
          )}

          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>Transporteigenschaften</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Ordnen Sie unsere Fahrzeugmerkmale den Transporteigenschaften in
            Ihrem DMRZ-Zugang zu. Nicht zugeordnete Merkmale bleiben beim Export
            außen vor — lieber eine Eigenschaft weniger als eine abgewiesene
            Datei.
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(SPECIAL_CAPABILITIES).map(([value, label]) => (
              <Grid key={value} size={{ xs: 12, sm: 6 }}>
                <TextField
                  label={label}
                  value={asText((config.dmrzCapabilityMap || {})[value])}
                  onChange={updateMapEntry('dmrzCapabilityMap', value)}
                  helperText="Name der Transporteigenschaft bei DMRZ"
                  fullWidth
                  size="small"
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )
    },
    {
      key: 'handover',
      label: 'Übergabeweg',
      completed: !!config.exportMethod,
      nextPatch: { exportMethod: 'csv' },
      content: (
        <Box>
          <Alert severity="info" sx={{ mb: 2 }}>
            <AlertTitle>Derzeit: Datei-Übergabe</AlertTitle>
            Sie exportieren die abrechnungsbereiten Fahrten im Tab „Fahrten"
            als CSV und laden sie in Ihrem Abrechnungszentrum hoch.
          </Alert>
          <Alert severity="warning">
            Eine direkte Schnittstelle (Übergabe per API und Rückmeldung der
            bewerteten Beträge) ist in Vorbereitung und hängt an einer
            offenen Rückfrage bei DMRZ bzw. opta data. Zugangsdaten werden
            deshalb hier bewusst noch nicht abgefragt — und sie werden
            später auch nicht in diesen Einstellungen gespeichert, da diese
            für die mobile App öffentlich lesbar sind.
          </Alert>

          {isDmrz && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                Feinheiten des Dateiformats
              </Typography>
              {/* Spezifikation v1.1 und DMRZ-Beispieldatei widersprechen sich
                  an genau diesen Punkten; die Klärung steht noch aus. Deshalb
                  umschaltbar statt fest verdrahtet — beim Einrichtungsgespräch
                  ist es eine Auswahl statt eines Software-Updates. */}
              <Alert severity="info" sx={{ mb: 2 }}>
                Die Voreinstellung folgt der DMRZ-Spezifikation. Ändern Sie hier
                nur etwas, wenn DMRZ Ihre Datei beanstandet oder Ihnen beim
                Einrichten eine andere Variante nennt.
              </Alert>
              <Grid container spacing={2}>
                {[
                  { field: 'encoding', label: 'Zeichensatz' },
                  { field: 'quoteFields', label: 'Felder in Anführungszeichen' },
                  { field: 'durationFormat', label: 'Format der Fahrtdauer' },
                  { field: 'headerVariant', label: 'Spaltenüberschriften' },
                  { field: 'waitSource', label: 'Bedeutung von „WARTEN"' }
                ].map(item => (
                  <Grid key={item.field} size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id={`dmrz-format-${item.field}`}>{item.label}</InputLabel>
                      <Select
                        labelId={`dmrz-format-${item.field}`}
                        label={item.label}
                        value={String(format[item.field])}
                        onChange={updateFormatField(item.field)}
                      >
                        {DMRZ_FORMAT_OPTIONS[item.field].map(option => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                ))}
              </Grid>
            </>
          )}
        </Box>
      )
    },
    {
      key: 'check',
      label: 'Prüflauf',
      content: (
        <Box>
          {/* Ohne geladene Fahrten wären alle Prüfungen trivial grün —
              das wäre eine falsche Entwarnung. */}
          {ridesLoading ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Fahrten werden geladen — der Prüflauf startet gleich.
            </Alert>
          ) : readiness.relevantCount === 0 ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Im geladenen Zeitraum gibt es keine offenen Krankenfahrten.
              Die fahrtbezogenen Prüfungen sind deshalb ohne Aussage — die
              Stammdaten-Prüfungen unten gelten trotzdem.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Geprüft werden {readiness.relevantCount} noch nicht bezahlte
              Krankenfahrten im aktuell geladenen Zeitraum.
            </Typography>
          )}
          <Stack spacing={1}>
            {readiness.checks.map(check => {
              // Fahrtbezogene Prüfungen ohne Datengrundlage nicht als
              // bestanden ausweisen.
              const inconclusive =
                !check.global && (ridesLoading || readiness.relevantCount === 0);
              const ok = !inconclusive && check.problems === 0;
              return (
                <Paper key={check.key} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" spacing={1.5} alignItems="flex-start">
                    {inconclusive
                      ? <PendingIcon color="disabled" fontSize="small" />
                      : ok
                        ? <OkIcon color="success" fontSize="small" />
                        : (check.global ? <ProblemIcon color="error" fontSize="small" /> : <WarnIcon color="warning" fontSize="small" />)}
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {check.label}
                      </Typography>
                      {inconclusive ? (
                        <Typography variant="caption" color="text.secondary">
                          Keine Fahrten zum Prüfen geladen.
                        </Typography>
                      ) : !ok && (
                        <Typography variant="caption" color="text.secondary">
                          {check.global
                            ? check.detail
                            : `${check.problems} Fahrt${check.problems === 1 ? '' : 'en'} betroffen — ${check.detail}`}
                        </Typography>
                      )}
                    </Box>
                  </Stack>
                </Paper>
              );
            })}
          </Stack>
          <Divider sx={{ my: 2 }} />
          <Typography variant="caption" color="text.secondary">
            Offene Punkte blockieren den Export nicht — sie sind die Liste
            dessen, was das Abrechnungszentrum vermutlich beanstanden würde.
          </Typography>
        </Box>
      )
    }
  ].filter(Boolean);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6">Abrechnungszentrum einrichten</Typography>
          {savedAt && <Chip size="small" color="success" variant="outlined" label="Gespeichert" />}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Diese Angaben brauchen wir, damit Ihr Export beim Abrechnungszentrum
          angenommen wird. Sie können den Assistenten jederzeit erneut
          durchlaufen — bestehende Werte bleiben erhalten.
        </Typography>

        <Stepper activeStep={activeStep} orientation="vertical" nonLinear>
          {steps.map((step, index) => (
            <Step key={step.key} completed={step.completed === true}>
              <StepLabel onClick={() => setActiveStep(index)} sx={{ cursor: 'pointer' }}>
                {step.label}
              </StepLabel>
              <StepContent>
                {step.content}
                <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                  {index > 0 && (
                    <Button onClick={() => setActiveStep(index - 1)}>Zurück</Button>
                  )}
                  {index === steps.length - 1 ? (
                    <Button
                      variant="contained"
                      disabled={saving}
                      onClick={() => persist({ setupCompletedAt: serverTimestamp() })}
                    >
                      Einrichtung abschließen
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      disabled={saving || step.nextDisabled === true}
                      onClick={() => saveAndNext(step.nextPatch)}
                    >
                      Weiter
                    </Button>
                  )}
                </Stack>
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {isDmrz && (
          // Pflichthinweis aus den API-AGB Ziff. 5e. Er gehört an eine gut
          // sichtbare Stelle der Anwendung — die Einrichtungsseite ist die,
          // die jeder DMRZ-Kunde zwangsläufig sieht.
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            Powered by DMRZ.de
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default MedicalBillingSetupTab;
